import { Component } from "@theme/component";
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  calculateHeaderGroupHeight,
} from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRow - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRow"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * The scrollTop when scroll direction changed to up
   * @type {number | null}
   */
  #scrollUpStartPosition = null;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 0;
  #headerGroupHeight = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty(
      "--header-height",
      `${roundedHeaderHeight}px`,
    );

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }

    // compute height of all the children elements of header group
    this.#headerGroupHeight = calculateHeaderGroupHeight(this);
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        if (this.dataset.themeColor)
          changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove("desktop:hidden");
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add("hidden");
    } else {
      this.refs.headerDrawerContainer.classList.add("desktop:hidden");
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove("hidden");
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== "always") return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;

    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;
    const scrollUpThreshold = 120;

    // When within header group height, always show the header
    const withinHeaderGroupHeight = scrollTop <= this.#headerGroupHeight;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      if (isAtTop) {
        this.dataset.scrollDirection = "none";
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // Detect direction change
    const wasScrollingDown =
      this.dataset.scrollDirection === "down" ||
      this.dataset.scrollDirection === "none";

    if (isScrollingUp) {
      // Track when scroll-up starts
      if (wasScrollingDown) {
        this.#scrollUpStartPosition = scrollTop;
      }

      // Calculate how much scrolled up
      const scrolledUpDistance = this.#scrollUpStartPosition
        ? this.#scrollUpStartPosition - scrollTop
        : 0;

      // Use dynamic threshold: if we're close to top, use a smaller threshold
      const dynamicThreshold = Math.min(
        scrollUpThreshold,
        this.#scrollUpStartPosition || scrollUpThreshold,
      );

      this.removeAttribute("data-animating");

      if (
        isAtTop ||
        (withinHeaderGroupHeight && this.dataset.stickyState !== "active")
      ) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
        this.#scrollUpStartPosition = null;
      } else if (
        withinHeaderGroupHeight &&
        this.dataset.stickyState !== "idle"
      ) {
        // If within header group height and header is already visible, don't change state to avoid jerky animation
        this.#scrollUpStartPosition = null;
      } else if (scrolledUpDistance >= dynamicThreshold) {
        // show sticky header only after scrolling up threshold distance
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      } else {
        // Keep hidden until threshold is reached
        this.dataset.scrollDirection = "up";
      }
    } else {
      // Reset scroll-up start position when scrolling down
      this.#scrollUpStartPosition = null;

      if (this.dataset.stickyState === "active") {
        this.dataset.scrollDirection = "none";
        // delay transitioning to idle hidden state for hiding animation
        this.setAttribute("data-animating", "");

        this.#timeout = setTimeout(() => {
          this.dataset.stickyState = "idle";
          this.removeAttribute("data-animating");
        }, this.#animationDelay);
      } else {
        this.dataset.scrollDirection = "none";
        this.dataset.stickyState = "idle";
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    //this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    //this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load
  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute("transparent") &&
            header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty(
        "--header-group-height",
        `${roundedHeaderGroupHeight}px`,
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
